<script setup lang="ts">
import { watch, ref, onMounted, onUnmounted } from 'vue';
import { useTimesheetStore, BreakType } from '@/stores/timesheets';
import { useRosterStore } from '@/stores/rosters';
import { useUserStore } from '@/stores/user';
import {
  useEmployeeWithTimesheetsStore,
  ShiftStatus,
} from '@/stores/employeesWithTimesheets/employeesWithTimesheets';
import { useTimesheetSetupStore } from '@/stores/timesheetSetup';
import { CoButton, CoIcon } from '@deputy/copilot2';
import { getTranslations } from '@/lib/util/i18n';
import LoadingSpinner from './LoadingSpinner.vue';
import ShiftDetails from './ShiftDetails.vue';
import BreakDetails from './BreakDetails.vue';
import ShiftActions from './ShiftActions.vue';
import ShiftDetailLineItem from './ShiftDetailLineItem.vue';
import { DateTime } from 'luxon';
import { DisplayTiming } from '@/lib/request/types/timesheetSetup';
import { setOnTheMinuteInterval } from '@/lib/util/dateTime';

const t = getTranslations({
  app: '',
  module: 'views',
  file: 'shift-card',
});

const timesheets = useTimesheetStore();
const rosters = useRosterStore();
const user = useUserStore();
const employeeWithTimesheets = useEmployeeWithTimesheetsStore();
const timesheetSetup = useTimesheetSetupStore();

const shiftElapsedTimeMinutes = ref(0);
const shiftElapsedTimeHours = ref(0);
const breakElapsedTimeMinutes = ref(0);
const breakElapsedTimeHours = ref(0);

const SECONDS_IN_MINUTE = 60;

watch(
  () => timesheets.isLoading == false,
  () => {
    if (timesheets.isOnShift) {
      if (!timesheets.isOnBreak) {
        shiftElapsedTimeHours.value = timesheets.getElapsedIntegerHours(
          DateTime.now().toMillis()
        );

        shiftElapsedTimeMinutes.value = timesheets.getElapsedRemainderMinutes(
          DateTime.now().toMillis()
        );
      } else {
        breakElapsedTimeHours.value =
          timesheets.getElapsedIntegerHoursOnBreak();
        breakElapsedTimeMinutes.value =
          timesheets.getElapsedRemainderMinutesOnBreak();
      }
    }
  }
);

function getEmployeeStatus() {
  return employeeWithTimesheets.getStatusValueForEmployee(
    user.activeUserOrThrow.Id
  );
}

function updateTime() {
  shiftElapsedTimeHours.value = timesheets.getElapsedIntegerHours(
    DateTime.now().toMillis()
  );

  shiftElapsedTimeMinutes.value = timesheets.getElapsedRemainderMinutes(
    DateTime.now().toMillis()
  );

  if (timesheets.isOnShift && !timesheets.isOnBreak) {
    if (shiftElapsedTimeMinutes.value == 60) {
      shiftElapsedTimeMinutes.value = 0;
      ++shiftElapsedTimeHours.value;
    }
  } else if (timesheets.isOnBreak) {
    breakElapsedTimeMinutes.value =
      timesheets.getElapsedRemainderMinutesOnBreak(
        DateTime.now().toSeconds() / SECONDS_IN_MINUTE
      );
    if (breakElapsedTimeMinutes.value == 60) {
      breakElapsedTimeMinutes.value = 0;
      ++breakElapsedTimeHours.value;
    }
  }
}

function actionTypeTranslation() {
  if (!timesheets.isOnShift && rosters.getNextRosterForActiveUser()) {
    return getEmployeeStatus() == ShiftStatus.Late
      ? t('late')
      : t('starting-soon');
  } else if (!timesheets.isOnShift) {
    return t('no-shift');
  } else if (timesheets.isOnShift && !timesheets.isOnBreak) {
    return t('on-shift');
  } else {
    return t('on-break');
  }
}

function timeTranslation() {
  let minutesTranslation: string;
  let hoursTranslation: string;
  if (!timesheets.isOnShift && getEmployeeStatus() == ShiftStatus.Late) {
    minutesTranslation =
      rosters.getLateTimeRemainderMinutes() != 1
        ? t('timer-minutes-plural')
        : t('timer-minutes');
    hoursTranslation =
      rosters.getLateTimeIntegerHours() != 1
        ? t('timer-hours-plural')
        : t('timer-hours');
  } else if (!timesheets.isOnBreak) {
    minutesTranslation =
      shiftElapsedTimeMinutes.value != 1
        ? t('timer-minutes-plural')
        : t('timer-minutes');
    hoursTranslation =
      shiftElapsedTimeHours.value != 1
        ? t('timer-hours-plural')
        : t('timer-hours');
  } else {
    minutesTranslation =
      breakElapsedTimeMinutes.value != 1
        ? t('timer-minutes-plural')
        : t('timer-minutes');
    hoursTranslation =
      breakElapsedTimeHours.value != 1
        ? t('timer-hours-plural')
        : t('timer-hours');
  }
  return { hours: hoursTranslation, minutes: minutesTranslation };
}

const props = defineProps<{
  area?: any;
}>();

let onTheMinuteIntervalPromise: any;

onMounted(async () => {
  if (props.area) {
    await timesheetSetup.load(
      timesheets.isOnShift
        ? DisplayTiming.ClockOutAndUpdate
        : DisplayTiming.ClockIn,
      props.area.Id
    );
  }

  onTheMinuteIntervalPromise = setOnTheMinuteInterval(() => {
    // todo: this has been added to address the race condition
    if (user.isAuthenticated() && timesheets.isOnShift) {
      updateTime();
    }
  });
});

onUnmounted(async () => {
  if (onTheMinuteIntervalPromise) {
    const [shiftTimeInterval, shiftTimeTimeout] =
      await onTheMinuteIntervalPromise;
    clearInterval(shiftTimeInterval);
    clearTimeout(shiftTimeTimeout);
  }
});

const emit = defineEmits<{
  (
    e: 'startShift',
    areaId: number,
    shiftQuestionsResponses?: Record<string, string>
  ): void;
  (e: 'startBreak', breakType: string, breakId?: number): void;
  (e: 'showBreaks'): void;
  (e: 'showAreas'): void;
  (e: 'showEditArea'): void;
  (e: 'showEditShift'): void;
  (e: 'showEditBreak'): void;
  (e: 'reloadShiftCard'): void;
  (e: 'showEndShiftInformation'): void;
  (e: 'showEndBreakConfirmation'): void;
  (e: 'showEditShiftQuestions'): void;
}>();
</script>

<template>
  <div class="shift-card">
    <LoadingSpinner
      v-if="
        timesheets.isLoading || rosters.isLoading || timesheetSetup.isLoading
      "
    />
    <div
      class="__content"
      v-else-if="!timesheets.hasAnyErrors"
      data-test-id="shift"
    >
      <div class="__action-card">
        <div v-if="timesheets.isOnShift" class="__duration">
          <span
            v-if="
              (!timesheets.isOnBreak && shiftElapsedTimeHours > 0) ||
              (timesheets.isOnBreak && breakElapsedTimeHours > 0)
            "
          >
            {{
              timesheets.isOnBreak
                ? breakElapsedTimeHours
                : shiftElapsedTimeHours
            }}
            {{ timeTranslation().hours }}
          </span>
          <span>
            {{
              timesheets.isOnBreak
                ? breakElapsedTimeMinutes
                : shiftElapsedTimeMinutes
            }}
            {{ timeTranslation().minutes }}
          </span>
        </div>
        <div
          v-else-if="getEmployeeStatus() == ShiftStatus.Late"
          class="__duration"
        >
          <span v-if="rosters.getLateTimeIntegerHours() > 0">
            {{ rosters.getLateTimeIntegerHours() }}
            {{ timeTranslation().hours }}
          </span>
          <span>
            {{ rosters.getLateTimeRemainderMinutes() }}
            {{ timeTranslation().minutes }}
          </span>
        </div>
        <div class="__type">{{ actionTypeTranslation() }}</div>
      </div>
      <div class="__details">
        <div
          v-if="
            timesheets.activeTimesheet && timesheetSetup.canModifyTimesheetOnEnd
          "
        >
          <a
            @click="emit('showEditShift')"
            v-if="timesheetSetup.canAddEditTimesheet && !timesheets.isOnBreak"
            class="__edit-details"
          >
            <ShiftDetails></ShiftDetails>
            <CoIcon class="__edit-details-icon" name="pencil" />
          </a>
          <ShiftDetails v-else></ShiftDetails>
          <a
            v-if="!timesheets.isOnBreak"
            @click="emit('showEditBreak')"
            class="__edit-details"
            data-test-id="breaks"
          >
            <BreakDetails></BreakDetails>
            <CoIcon class="__edit-details-icon" name="pencil" />
          </a>
          <BreakDetails v-else></BreakDetails>
        </div>
        <div v-else>
          <ShiftDetails></ShiftDetails>
          <BreakDetails></BreakDetails>
        </div>
        <ShiftDetailLineItem
          data-test-id="area"
          v-if="
            timesheets.activeTimesheet || rosters.getNextRosterForActiveUser()
          "
          :canEdit="
            !timesheets.activeTimesheet &&
            timesheetSetup.canModifyTimesheetOnEnd &&
            !timesheetSetup.isClockInUnavailable()
          "
          @showEditModal="emit('showEditArea')"
          icon="briefcase"
          :infoContent="false"
          ><template #main-content>{{
            area?.OperationalUnitName
          }}</template></ShiftDetailLineItem
        >
        <ShiftDetailLineItem
          data-test-id="comment"
          v-if="
            rosters.getRosterForActiveTimesheet()?.Comment ||
            rosters.getNextRosterForActiveUser()?.Comment
          "
          icon="comment"
          :infoContent="false"
          ><template #main-content>{{
            rosters.getRosterForActiveTimesheet()
              ? rosters.getRosterForActiveTimesheet()?.Comment
              : rosters.getNextRosterForActiveUser()?.Comment
          }}</template></ShiftDetailLineItem
        >
        <ShiftDetailLineItem
          class="__shift-questions"
          v-if="
            timesheetSetup.getAllCustomFields()?.length && timesheets.isOnShift
          "
          :canEdit="true"
          icon="document"
          :infoContent="false"
          @showEditModal="emit('showEditShiftQuestions')"
          ><template #main-content
            >{{ timesheetSetup.getAllCustomFields().length }}
            {{
              timesheetSetup.getAllCustomFields().length != 1
                ? t('shift-questions-plural')
                : t('shift-questions')
            }}</template
          ></ShiftDetailLineItem
        >
      </div>
      <ShiftActions
        @showAreas="emit('showAreas')"
        @startShift="
          emit(
            'startShift',
            rosters.getNextRosterForActiveUser()!.OperationalUnit
          )
        "
        @showBreaks="emit('showBreaks')"
        @showEndBreakConfirmation="emit('showEndBreakConfirmation')"
        @showEndShiftInformation="emit('showEndShiftInformation')"
        @startBreak="
          emit(
            'startBreak',
            BreakType.Unpaid,
            timesheets.getUnfinishedUnpaidScheduledBreakId()
          )
        "
      ></ShiftActions>
    </div>
    <div class="__loading-error" v-else>
      <div class="__loading-error-message">{{ t('loading-failed') }}</div>
      <CoButton type="primary" size="large" @click="emit('reloadShiftCard')">{{
        t('reload')
      }}</CoButton>
    </div>
  </div>
</template>

<style scoped>
.shift-card {
  max-width: 808px;
  min-height: 304px;
  border: 1px solid #d8d7dc;
  border-radius: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  margin: 0 auto;
}

@media (max-width: 900px) {
  .shift-card {
    margin: 0 2rem;
  }
}
.__content {
  width: 100%;
}
.__action-card {
  display: flex;
  flex-direction: column;
  background: #f3f3f7;
  border-radius: 8px;
  min-height: 148px;
  justify-content: center;
  align-items: center;
  margin-bottom: 48px;
}
.__duration span {
  font-size: 32px;
  font-weight: 600;
  color: var(--co-text-primary);
}
.__type {
  margin-top: 8px;
  font-size: 24px;
  font-weight: 600;
  color: var(--co-text-secondary);
}

.__details {
  color: var(--co-text-primary);
  font-size: 17px;
  margin-bottom: 48px;
}
.__edit-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 12px;
  margin-bottom: 4px;
  border-radius: 8px;
}
.__edit-details .__edit-details-icon {
  margin-left: 13.5px;
  margin-right: 0;
  color: var(--co-shift-empty-border);
}

.__edit-details:hover {
  background-color: var(--co-background-secondary);
  text-decoration: none;
}

.__loading-error {
  text-align: center;
}
.__loading-error-message {
  color: var(--co-text-primary);
  margin-bottom: 30px;
}
</style>
<style>
.__edit-details:hover .__edit-details-icon {
  color: var(--co-text-highlight-hover);
}
.__shift-questions .__details-icon {
  width: 12px;
  margin-left: 5px;
}
</style>
